import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { fmtError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { ASSETS } from "@/i18n";
import { Loader2, Sparkles } from "lucide-react";

const LEVEL_COLORS = {
  Bronzo:  "from-[#FCE7C8] to-[#E8B27E]",
  Argento: "from-[#E5E7EB] to-[#A1A1AA]",
  Oro:     "from-[#FEF3C7] to-[#D4A437]",
  Platino: "from-[#E0E7FF] to-[#A78BFA]",
};
// colori per i livelli con altri nomi (quelli del gestionale), nell'ordine delle soglie
const GRADIENTS = [
  "from-[#E5E7EB] to-[#A1A1AA]", "from-[#E0E7FF] to-[#94A3B8]",
  "from-[#FEF3C7] to-[#D4A437]", "from-[#F5D0FE] to-[#A78BFA]",
];
// Stessi livelli predefiniti del gestionale (se il server non ne invia)
const DEFAULT_LEVELS = [
  { name: "Base", from: 0 }, { name: "Silver", from: 500 }, { name: "Gold", from: 1500 }, { name: "Platinum", from: 5000 },
];

export default function FidelityPage() {
  const { api } = useTenant();
  const [card, setCard] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!api) return;
    api.myFidelity().then(setCard).catch((e) => setError(fmtError(e)));
  }, [api]);

  if (!card) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="fidelity-loading">
        {error ? <div className="text-[#B91C1C]">{error}</div> : <Loader2 className="animate-spin text-[#A78BFA]" />}
      </div>
    );
  }

  // Livelli del centro (dal gestionale AURA, se collegato): nomi e soglie arrivano dal server
  const levels = card.levels?.length ? card.levels : DEFAULT_LEVELS;
  const idx = Math.max(0, levels.findIndex((lv) => lv.name === card.level));
  const levelGrad = LEVEL_COLORS[card.level] || GRADIENTS[idx % GRADIENTS.length];
  const next = levels[idx + 1];
  const progress = !next ? 100
    : Math.round(((card.lifetime_points - levels[idx].from) / Math.max(1, next.from - levels[idx].from)) * 100);

  return (
    <div data-testid="fidelity-page" className="max-w-4xl">
      <div className="text-[11px] tracking-[0.3em] uppercase text-[#A78BFA] mb-3">Fidelity card</div>
      <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-[#1A1A1A]">
        La tua <span className="italic">aura</span> in punti.
      </h1>
      <p className="mt-4 text-[#4A4A4A] font-light max-w-2xl">
        Ogni trattamento ti regala punti. Più punti, più premi: dal 10% di sconto al trattamento omaggio.
      </p>

      <div className="mt-10 grid lg:grid-cols-5 gap-6">
        {/* Card */}
        <div className="lg:col-span-3">
          <div
            data-testid="fidelity-card"
            className="relative h-full rounded-3xl overflow-hidden border border-black/5 p-8 md:p-10 text-white tilt-card"
            style={{ background: "linear-gradient(135deg, #1A1A1A 0%, #2A1F3D 55%, #1A1A1A 100%)" }}
          >
            <div aria-hidden className="pointer-events-none absolute -top-16 -right-16 h-56 w-56 rounded-full blur-3xl opacity-50 animate-blob"
              style={{ background: "radial-gradient(circle, #A78BFA 0%, transparent 70%)" }} />
            <div aria-hidden className="pointer-events-none absolute -bottom-20 -left-12 h-52 w-52 rounded-full blur-3xl opacity-45 animate-blob-slow"
              style={{ background: "radial-gradient(circle, #67E8F9 0%, transparent 70%)" }} />

            <div className="relative flex items-start justify-between">
              <div className="flex items-center gap-3">
                <img src={ASSETS.logo} alt="AURA" className="h-12 w-12 rounded-full object-contain bg-white/10 border border-white/15" />
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-white/60">Fidelity Card</div>
                  <div className="font-serif italic text-xl">AURA</div>
                </div>
              </div>
              <div className={`text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 rounded-full bg-gradient-to-r ${levelGrad} text-[#1A1A1A] font-medium`}>
                {card.level}
              </div>
            </div>

            <div className="relative mt-12">
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/60">Punti disponibili</div>
              <div className="font-serif font-light text-6xl mt-1" data-testid="fidelity-points">
                {card.points.toLocaleString("it-IT")}
              </div>
              <div className="text-xs text-white/60 mt-2">
                {card.points_to_next > 0
                  ? `${card.points_to_next} punti al livello successivo`
                  : "Hai raggiunto il livello massimo ✨"}
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#A78BFA] to-[#67E8F9] transition-all duration-700"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <div className="relative mt-10 flex items-end justify-between text-[10px] tracking-[0.3em] uppercase text-white/50">
              <span data-testid="fidelity-name">{card.client_name || "Cliente"}</span>
              <span>Lifetime: {card.lifetime_points}</span>
            </div>
          </div>
        </div>

        {/* QR */}
        <div className="lg:col-span-2 rounded-3xl border border-black/5 bg-white p-8 flex flex-col items-center justify-center text-center" data-testid="fidelity-qr">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#737373] mb-4">QR identificativo</div>
          <div className="bg-white p-3 rounded-2xl border border-black/5">
            <QRCodeSVG value={card.qr_token} size={180} bgColor="#ffffff" fgColor="#1A1A1A" level="M" />
          </div>
          <p className="mt-5 text-sm text-[#4A4A4A] font-light max-w-xs">
            Mostra questo codice in salone per accumulare punti automaticamente.
          </p>
        </div>
      </div>

      {/* Levels */}
      <div className="mt-12">
        <h2 className="font-serif text-3xl text-[#1A1A1A]">I livelli AURA</h2>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {levels.map((lv, i) => (
            <div
              key={lv.name}
              className={`rounded-3xl border p-6 ${
                card.level === lv.name ? "border-[#A78BFA] bg-white shadow-lg" : "border-black/5 bg-white"
              }`}
            >
              <div className={`h-10 w-10 rounded-full bg-gradient-to-br ${LEVEL_COLORS[lv.name] || GRADIENTS[i % GRADIENTS.length]}`} />
              <h3 className="mt-4 font-serif text-xl text-[#1A1A1A]">{lv.name}</h3>
              <div className="mt-1 text-xs text-[#737373]">da {lv.from} punti</div>
              {lv.discount_pct > 0 && <p className="mt-3 text-sm text-[#4A4A4A] font-light">−{lv.discount_pct}% sui trattamenti</p>}
              {card.level === lv.name && (
                <div className="mt-3 inline-flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase text-[#A78BFA]">
                  <Sparkles size={12} /> Sei qui
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
